import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Input,
  Label,
  Container,
  CardFooter,
  Badge,
  Col,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import AdminHeader from "components/Headers/AdminHeader";

const categoryList = [
  {
    id: 1,
    categoryName: "Technical & Trade Training",
    status: "ACTIVE",
    createdBy: "Admin",
  },
  {
    id: 2,
    categoryName: "Safety & Compliance",
    status: "ACTIVE",
    createdBy: "Admin",
  },
  {
    id: 3,
    categoryName: "Operations & Maintenance",
    status: "INACTIVE",
    createdBy: "Admin",
  },
  {
    id: 4,
    categoryName: "Quality & Inspection",
    status: "ACTIVE",
    createdBy: "Admin",
  },
  {
    id: 5,
    categoryName: "Soft Skills & Leadership",
    status: "ACTIVE",
    createdBy: "Admin",
  },
  {
    id: 6,
    categoryName: "Energy Management Basic & Advanced",
    status: "INACTIVE",
    createdBy: "Admin",
  },
];

const initialFields = {
  categoryName: { value: "", error: "" },
  status: { value: "ACTIVE", error: "" },
};

function Category() {
  const { notify } = useContext(NotificationContext);
  const [isModalOpen, setIsModalOpen] = useState(null); //"add "edit" "null"
  const [formData, setFormData] = useState(initialFields);

  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    name: "",
    status: null,
    filterParams: [],
  });

  const openAddModal = () => {
    // e.stopPropagation();
    setIsModalOpen("add");
    setFormData(initialFields);
  };

  const openEditModal = (category) => {
    setIsModalOpen("edit");
    setFormData({
      categoryName: { value: category.categoryName, error: "" },
      status: { value: category.status, error: "" },
    });
  };

  const closeModal = () => {
    setIsModalOpen(null);
    setFormData({
      ...initialFields,
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
  };

  const toggleStatus = (category) => {
    const isActive = category.status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this category?`,
      message: `Category "${
        category.categoryName
      }" will become ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => {},
    });
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    { Header: "Category Name", accessor: "categoryName" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => {
        const category = row.original;
        const id = category.id;
        const status = category.status;
        const isActive = status === "ACTIVE";

        return (
          <div className="d-flex justify-content-start align-items-center">
            {/* EDIT */}
            <Button
              color="link"
              size="sm"
              className="p-0"
              onClick={() => openEditModal(category)}
              id={`edit-${id}`}
            >
              <i className="fas fa-edit mr-1 table-action-info" />
            </Button>
            <UncontrolledTooltip delay={0} target={`edit-${id}`}>
              Edit
            </UncontrolledTooltip>
            {/* Divider */}
            <span className="mr-1 text-muted">|</span>

            {/* Activate / Deactivate */}
            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${id}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(category);
              }}
            >
              {isActive ? (
                <>
                  <i className="fa-regular fa-circle-xmark table-action-delete mr-1" />
                </>
              ) : (
                <>
                  <i className="fa-regular fa-circle-check table-action-success mr-1" />
                </>
              )}
            </Button>
            <UncontrolledTooltip delay={0} target={`status${id}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleClear = () => {
    setSearchState({
      query: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {};

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "categoryName":
        return !trimmedValue ? "Please enter category name" : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newCategory = { ...formData };

    Object.keys(newCategory).forEach((key) => {
      newCategory[key] = {
        ...newCategory[key],
        error: getValidationError(key, newCategory[key].value),
      };
    });

    setFormData(newCategory);
    return Object.values(newCategory).every((item) => item.error === "");
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <CardTitle tag="h3" className="mb-0">
            Course Category
            </CardTitle>

            <Button className="btn-deep-blue" onClick={openAddModal}>
              <i className="fa fa-plus mr-1" />
            Course Category
            </Button>
          </CardHeader>
          {/* Body */}
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
              {/* Left side - Filter Inputs + Search */}
              <div className="d-flex flex-wrap align-items-center filter-group">
                <Input
                  type="text"
                  placeholder="Search by category name"
                  className="mr-2 mb-2 filter-input-large"
                  value={searchState.searchString}
                  onChange={(e) =>
                    setSearchState((prev) => ({
                      ...prev,
                      searchString: e.target.value,
                    }))
                  }
                  onKeyDown={handleKeyDown}
                />
                <Select
                  className="mr-2 mb-2 filter-select"
                  options={STATUS_OPTIONS}
                  placeholder="Select Status"
                  classNamePrefix="react-select"
                  styles={fixedWidthCustomStyle()}
                  components={{ Option, SingleValue }}
                />

                <Button
                  color="default"
                  // className="d-flex align-items-center btn-deep-blue mb-0"
                  className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                  onClick={handleSearch}
                >
                  <i className="fa fa-search mr-1" />
                  Search
                </Button>

                {Object.keys(searchState.filterParams).length > 0 && (
                  <Button
                    color="default"
                    className="ml-2 mb-0 filter-btn btn-simple"
                    onClick={handleClear}
                  >
                    <i className="fa fa-times mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-2 mb-3 mx-1">
              {categoryList.length ? (
                <span className="total-records">
                  Total Records: {categoryList.length}
                </span>
              ) : null}
            </div>

            <div className="table-responsive">
              <CommonTable
                data={categoryList || []}
                columns={columns}
                pageNum={pageNum}
                setPageNum={setPageNum}
                defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                showPaginationBottom
              />
            </div>
          </CardBody>
        </Card>

        <RightPanelCard isOpen={isModalOpen !== null} onClose={closeModal}>
          <Card>
            <CardHeader tag="div" className="p-3">
              <div className="d-flex align-items-center justify-content-between mt-2">
                <div className="d-flex align-items-center">
                  <i
                    className="fa-solid fa-angle-left cursor mr-2"
                    onClick={closeModal}
                  ></i>
                  <h4 className="mb-0">
                    {isModalOpen === "add" ? "Add Skill" : "Edit Skill"}
                  </h4>
                </div>

                <div className="d-flex justify-content-end">
                  <i
                    className="fa-solid fa-xmark cursor text-muted"
                    onClick={closeModal}
                  ></i>
                </div>
              </div>
            </CardHeader>

            <Form onSubmit={(e) => e.preventDefault()}>
              <CardBody>
                <Row>
                  <Col md="6" className="mb-3">
                    <FormGroup>
                      <Label>
                        Skill Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="categoryName"
                        type="text"
                        placeholder="Enter Category"
                        value={formData.categoryName.value}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          formData.categoryName.error ? "is-invalid" : ""
                        }
                      />
                      {formData.categoryName.error && (
                        <small className="text-danger">
                          {formData.categoryName.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="6" className="mb-3">
                    <FormGroup>
                      <Label>
                        Status <span className="text-danger">*</span>
                      </Label>
                      <div className="radio-rapper">
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="ACTIVE"
                              checked={formData.status.value === "ACTIVE"}
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />{" "}
                            Active
                          </Label>
                        </FormGroup>
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="INACTIVE"
                              checked={formData.status.value === "INACTIVE"}
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />{" "}
                            Inactive
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>

              <CardFooter className="text-right">
                <Button color="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button color="deep-blue" onClick={handleSubmit}>
                  Save
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
}

export default Category;
